import { useMemo, useState, useEffect, useRef } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import {
  Box,
  CircularProgress,
  Paper,
  Typography,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useContextMenuMESF } from "@dexteel/mesf-core";
import type { MoldMapPanelProps, MoldData, StrandCardData } from "../types";
import { MoldSelector } from "./MoldSelector";
import { MoldModel } from "./MoldModel";
import {
  CAMERA_SETTINGS,
  ORBIT_CONTROLS_SETTINGS,
  LIGHTING_SETTINGS,
} from "../constants";
import { ShadowMapSettings } from "../../../../../shared/theme";
import type { CasterTreeRow } from "../../../models";
import {
  STRAND_CONTAINER_IDS,
  isStrandContainer,
  getStrandName,
} from "../../../models";
import { useCasterContextMenu } from "../../../hooks/useCasterContextMenu";

export const MoldMapPanel = ({
  treeData,
  selectedRow,
  onSelectRow,
  onSelectStrand,
  onSetOffline,
  onInstallMoldToStrand,
  onInstall,
  onUninstall,
  onRepair,
  onNewSparePart,
  onEditPart,
  onSetInitialMetric,
  isLoading = false,
}: MoldMapPanelProps) => {
  const theme = useTheme();

  const { getMenuOptions } = useCasterContextMenu({
    onInstall,
    onUninstall,
    onRepair,
    onNewSparePart,
    onEditPart,
    onSetInitialMetric,
    onCorrectDates: () => {}, // Not applicable in map view
    onSetOnline: () => {}, // Handled separately via MoldSelector
    onSetOffline: () => {}, // Handled separately via MoldSelector
  });

  const { showContextMenu, registerConfig } = useContextMenuMESF();

  // Use a ref to keep getMenuOptions up-to-date without triggering re-registration
  const getMenuOptionsRef = useRef(getMenuOptions);
  getMenuOptionsRef.current = getMenuOptions;

  // Register context menu config only once on mount
  useEffect(() => {
    registerConfig({
      id: "moldMapContext",
      getOptions: (data: unknown) =>
        getMenuOptionsRef.current(data as CasterTreeRow),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // State for selected strand (drives card highlighting + right panel)
  const [selectedStrandId, setSelectedStrandId] = useState<number | null>(null);

  // State for selected mold in the 3D view
  // Initialize from selectedRow if it's an equipment or location
  const [selectedMoldId, setSelectedMoldId] = useState<number | null>(() => {
    if (
      selectedRow &&
      (selectedRow.rowType === "equipment" ||
        selectedRow.rowType === "location")
    ) {
      return selectedRow.EquipmentId;
    }
    return null;
  });

  // State for hovered location
  const [hoveredData, setHoveredData] = useState<CasterTreeRow | null>(null);
  const [hoveredLocationId, setHoveredLocationId] = useState<string | null>(
    null,
  );

  // Transform tree data into MoldData array
  // Mold equipment rows have rowType="equipment"; their children are location rows
  const molds: MoldData[] = useMemo(() => {
    const moldMap = new Map<number, MoldData>();

    for (const row of treeData) {
      if (row.rowType === "equipment") {
        // This is a mold equipment row
        moldMap.set(row.EquipmentId, {
          moldId: row.EquipmentId,
          moldName: row.EquipmentName ?? `Mold #${row.EquipmentId}`,
          isOnline: row.Status === "Online",
          heats: row.Heats ?? 0,
          totalHeats: row.TotalHeats ?? 0,
          locations: [],
        });
      } else if (row.rowType === "location") {
        // This is a location row (component position on a mold)
        const mold = moldMap.get(row.EquipmentId);
        if (mold) {
          mold.locations.push(row);
        }
      }
    }

    return Array.from(moldMap.values());
  }, [treeData]);

  // Build strand-based card data (always 5 entries, one per strand)
  const strands: StrandCardData[] = useMemo(() => {
    return STRAND_CONTAINER_IDS.map((strandId) => {
      const strandName = getStrandName(treeData, strandId);
      // Find the online mold installed in this strand
      const moldRow = treeData.find(
        (row) =>
          row.rowType === "equipment" &&
          row.StrandPartId === strandId &&
          row.Status === "Online",
      );
      const mold = moldRow
        ? (molds.find((m) => m.moldId === moldRow.EquipmentId) ?? null)
        : null;
      return { strandId, strandName, mold };
    });
  }, [treeData, molds]);

  // Auto-select the first occupied strand if none selected
  useEffect(() => {
    if (selectedStrandId === null) {
      const firstOccupied = strands.find((s) => s.mold !== null);
      if (firstOccupied) {
        setSelectedStrandId(firstOccupied.strandId);
        if (firstOccupied.mold) {
          setSelectedMoldId(firstOccupied.mold.moldId);
        }
      }
    }
  }, [strands, selectedStrandId]);

  // Sync selectedMoldId and selectedStrandId when selectedRow changes
  // from the tree view. Keeps 3D viewer + card highlighting in sync.
  useEffect(() => {
    if (!selectedRow) return;

    // Equipment row (mold) → select that mold if online, highlight its strand
    if (
      selectedRow.rowType === "equipment" &&
      selectedRow.Status === "Online"
    ) {
      setSelectedMoldId(selectedRow.EquipmentId);
      const strand = strands.find(
        (s) => s.mold?.moldId === selectedRow.EquipmentId,
      );
      if (strand) setSelectedStrandId(strand.strandId);
      return;
    }

    // Location row → select the parent online mold, highlight its strand
    if (selectedRow.rowType === "location") {
      const parentMold = molds.find(
        (m) => m.moldId === selectedRow.EquipmentId && m.isOnline,
      );
      if (parentMold) {
        setSelectedMoldId(parentMold.moldId);
        const strand = strands.find(
          (s) => s.mold?.moldId === parentMold.moldId,
        );
        if (strand) setSelectedStrandId(strand.strandId);
      }
      return;
    }

    // Strand container → highlight it, select its mold for 3D
    if (
      selectedRow.rowType === "container" &&
      selectedRow.ContainerId &&
      isStrandContainer(selectedRow.ContainerId)
    ) {
      setSelectedStrandId(selectedRow.ContainerId);
      const strand = strands.find(
        (s) => s.strandId === selectedRow.ContainerId,
      );
      if (strand?.mold) {
        setSelectedMoldId(strand.mold.moldId);
      } else {
        // Empty strand → clear the 3D mold
        setSelectedMoldId(null);
      }
    }
  }, [selectedRow, molds, strands]);

  // Get the currently selected mold data
  const selectedMold = useMemo(() => {
    return molds.find((m) => m.moldId === selectedMoldId) ?? null;
  }, [molds, selectedMoldId]);

  // Get selected location ID from selectedRow
  const selectedLocationId = useMemo(() => {
    if (!selectedRow || selectedRow.rowType === "equipment") return null;
    return selectedRow.id;
  }, [selectedRow]);

  // Handle strand selection from MoldSelector
  const handleSelectStrand = (strandId: number) => {
    setSelectedStrandId(strandId);

    const strand = strands.find((s) => s.strandId === strandId);
    let moldRow: CasterTreeRow | null = null;

    if (strand?.mold) {
      // Show the mold in the 3D view
      setSelectedMoldId(strand.mold.moldId);
      // Find the mold equipment row so the tree can highlight it
      moldRow =
        treeData.find(
          (row) =>
            row.rowType === "equipment" &&
            row.EquipmentId === strand.mold!.moldId,
        ) ?? null;
    } else {
      // Empty strand → clear 3D mold
      setSelectedMoldId(null);
    }

    // Report strand selection to parent (CasterMoldPage sets selectedStrandId + selectedRow)
    onSelectStrand(strandId, moldRow);
  };

  // Handle location selection from 3D model
  const handleSelectLocation = (row: CasterTreeRow | null) => {
    onSelectRow(row);
  };

  // Handle location hover from 3D model
  const handleHoverLocation = (locationId: string | null) => {
    setHoveredLocationId(locationId);

    if (locationId === null) {
      setHoveredData(null);
      return;
    }

    // Find the location data by id
    const locationData = selectedMold?.locations.find(
      (loc) => loc.id === locationId,
    );

    setHoveredData(locationData ?? null);
  };

  // Handle context menu on canvas
  const handleCanvasContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    // If hovering over a location, show context menu
    if (hoveredData && hoveredData.rowType === "location") {
      onSelectRow(hoveredData);
      showContextMenu(event, hoveredData, "moldMapContext");
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: (theme) => theme.palette.background.paper,
      }}
    >
      {/* Strand selector bar at top */}
      <MoldSelector
        strands={strands}
        selectedStrandId={selectedStrandId}
        onSelectStrand={handleSelectStrand}
        onSetOffline={onSetOffline}
        onInstallMoldToStrand={onInstallMoldToStrand}
      />

      {/* 3D Canvas or Empty Strand placeholder */}
      <Box
        sx={{
          flex: 1,
          position: "relative",
          minHeight: 400,
        }}
        onContextMenu={selectedMold ? handleCanvasContextMenu : undefined}
      >
        {isLoading ? (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: (theme) =>
                theme.palette.eqp.viewer.loadingOverlay,
              zIndex: 10,
            }}
          >
            <CircularProgress />
          </Box>
        ) : null}

        {selectedMold ? (
          <Canvas
            style={{
              background: `linear-gradient(180deg, ${theme.palette.eqp.viewer.canvasGradient.start} 0%, ${theme.palette.eqp.viewer.canvasGradient.middle} 50%, ${theme.palette.eqp.viewer.canvasGradient.end} 100%)`,
            }}
            camera={{
              position: CAMERA_SETTINGS.position,
              fov: CAMERA_SETTINGS.fov,
              near: CAMERA_SETTINGS.near,
              far: CAMERA_SETTINGS.far,
            }}
            shadows
          >
            <ambientLight intensity={LIGHTING_SETTINGS.ambientIntensity} />
            <directionalLight
              position={LIGHTING_SETTINGS.directionalPosition}
              intensity={LIGHTING_SETTINGS.directionalIntensity}
              castShadow
              shadow-mapSize-width={ShadowMapSettings.SIZE}
              shadow-mapSize-height={ShadowMapSettings.SIZE}
            />

            <OrbitControls
              enablePan={ORBIT_CONTROLS_SETTINGS.enablePan}
              enableZoom={ORBIT_CONTROLS_SETTINGS.enableZoom}
              enableRotate={ORBIT_CONTROLS_SETTINGS.enableRotate}
              minDistance={ORBIT_CONTROLS_SETTINGS.minDistance}
              maxDistance={ORBIT_CONTROLS_SETTINGS.maxDistance}
              minPolarAngle={ORBIT_CONTROLS_SETTINGS.minPolarAngle}
              maxPolarAngle={ORBIT_CONTROLS_SETTINGS.maxPolarAngle}
              enableDamping={ORBIT_CONTROLS_SETTINGS.enableDamping}
              dampingFactor={ORBIT_CONTROLS_SETTINGS.dampingFactor}
              target={ORBIT_CONTROLS_SETTINGS.target}
            />

            <MoldModel
              moldData={selectedMold}
              selectedLocationId={selectedLocationId}
              hoveredLocationId={hoveredLocationId}
              onSelectLocation={handleSelectLocation}
              onHoverLocation={handleHoverLocation}
            />
          </Canvas>
        ) : (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `linear-gradient(180deg, ${theme.palette.eqp.viewer.canvasGradient.start} 0%, ${theme.palette.eqp.viewer.canvasGradient.middle} 50%, ${theme.palette.eqp.viewer.canvasGradient.end} 100%)`,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 500,
              }}
            >
              Empty Strand
            </Typography>
          </Box>
        )}

        {/* Screen-space tooltip for hovered location */}
        {hoveredData ? (
          <Box
            sx={{
              position: "absolute",
              top: 10,
              left: 10,
              zIndex: 20,
              pointerEvents: "none",
            }}
          >
            <Paper
              elevation={4}
              sx={{
                padding: (theme) => theme.spacing(1, 1.5),
                backgroundColor: (theme) =>
                  theme.palette.eqp.viewer.tooltipBackground,
                color: (theme) => theme.palette.common.white,
                minWidth: 180,
                maxWidth: 250,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  marginBottom: (theme) => theme.spacing(0.5),
                }}
              >
                {hoveredData.PositionName ?? "Unknown Position"}
              </Typography>

              {hoveredData.HasAssignedPart ? (
                <>
                  {hoveredData.PartInfo?.SerialNumber ||
                  hoveredData.L2PartName ? (
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        color: (theme) => theme.palette.grey[400],
                        marginBottom: (theme) => theme.spacing(0.5),
                      }}
                    >
                      {hoveredData.PartInfo?.SerialNumber ??
                        hoveredData.L2PartName}
                    </Typography>
                  ) : null}
                  <Divider
                    sx={{
                      backgroundColor: (theme) => theme.palette.grey[700],
                      margin: (theme) => theme.spacing(0.5, 0),
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "0.8rem",
                      marginTop: (theme) => theme.spacing(0.25),
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ color: (theme) => theme.palette.grey[400] }}
                    >
                      Heats Since Install:
                    </Box>
                    <Box component="span" sx={{ fontWeight: 500 }}>
                      {hoveredData.Heats?.toLocaleString() ?? "-"}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "0.8rem",
                      marginTop: (theme) => theme.spacing(0.25),
                    }}
                  >
                    <Box
                      component="span"
                      sx={{ color: (theme) => theme.palette.grey[400] }}
                    >
                      Heats Since Repair:
                    </Box>
                    <Box component="span" sx={{ fontWeight: 500 }}>
                      {hoveredData.HeatsSinceRepair?.toLocaleString() ?? "-"}
                    </Box>
                  </Box>
                  {hoveredData.TotalHeats !== undefined ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: "0.8rem",
                        marginTop: (theme) => theme.spacing(0.25),
                      }}
                    >
                      <Box
                        component="span"
                        sx={{ color: (theme) => theme.palette.grey[400] }}
                      >
                        Total Heats:
                      </Box>
                      <Box component="span" sx={{ fontWeight: 500 }}>
                        {hoveredData.TotalHeats.toLocaleString()}
                      </Box>
                    </Box>
                  ) : null}
                </>
              ) : (
                <Typography
                  sx={{
                    fontSize: "0.8rem",
                    fontStyle: "italic",
                    color: (theme) => theme.palette.grey[500],
                  }}
                >
                  No part installed
                </Typography>
              )}
            </Paper>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
};
